/* Cookie consent banner — Iter 44 · Ley 1581 & GDPR-like UX.
 *
 * Stores decision in localStorage `habita.cookie_consent`:
 *   { version, level: 'all'|'necessary', accepted_at: ISO }
 *
 * Levels:
 *   - 'all'       → all cookies enabled (analytics + marketing)
 *   - 'necessary' → only strictly-necessary cookies
 *
 * Analytics libs (analytics.js) should read this before firing pixels.
 * A helper `hasCookieConsent(level)` is exported for that purpose.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'habita.cookie_consent';
export const COOKIE_POLICY_VERSION = '2026-02-01';

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== COOKIE_POLICY_VERSION) return null;   // require re-consent
    return parsed;
  } catch (e) {
    return null;
  }
}

export function hasCookieConsent(level = 'all') {
  const c = getCookieConsent();
  if (!c) return false;
  if (level === 'necessary') return true;
  return c.level === 'all';
}

function saveConsent(level) {
  const payload = { version: COOKIE_POLICY_VERSION, level, accepted_at: new Date().toISOString() };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* quota */ }
  // Broadcast so analytics.js can pick it up without page reload
  window.dispatchEvent(new CustomEvent('habita:cookie-consent', { detail: payload }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only when no valid consent exists yet
    if (!getCookieConsent()) {
      // Small delay so the banner doesn't fight the initial render
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const acceptAll = () => { saveConsent('all'); setVisible(false); };
  const onlyNecessary = () => { saveConsent('necessary'); setVisible(false); };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[80] p-4 pointer-events-none"
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
    >
      <div className="max-w-4xl mx-auto bg-habita-dark text-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto">
        <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-amber-400/20 text-amber-300 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p id="cookie-banner-title" className="font-semibold text-sm mb-1">
              Habita usa cookies para mejorar tu experiencia
            </p>
            <p className="text-xs text-white/70 leading-relaxed">
              Usamos cookies necesarias para el funcionamiento del sitio y analíticas para entender cómo lo usas.
              Puedes aceptar todas o solo las necesarias. Lee la{' '}
              <Link to="/cookies" className="underline hover:text-amber-300">Política de Cookies</Link>.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
            <button
              onClick={onlyNecessary}
              className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold border border-white/20 whitespace-nowrap"
              data-testid="cookie-only-necessary"
            >
              Solo necesarias
            </button>
            <button
              onClick={acceptAll}
              className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-xs font-semibold whitespace-nowrap"
              data-testid="cookie-accept-all"
            >
              Aceptar todas
            </button>
            <button
              onClick={onlyNecessary}
              aria-label="Cerrar banner"
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              data-testid="cookie-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
